import { BaseWithFollowUsLayout } from "../../layouts";
import { Container, FAQ, InlineLink, InternalInlineLink, NextPageWithLayout } from "@akte-mord/common";
import { FAQPageJsonLd } from "next-seo";
import { Paper, Typography } from "@mui/material";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import i18nConfig from "../../../next-i18next.config";
import React from "react";
import type { GetStaticProps } from "next";

const Help: NextPageWithLayout = () => (
  <React.Fragment>
    <FAQPageJsonLd
      mainEntity={[
        {
          questionName: "Wie funktioniert Akte Mord?",
          acceptedAnswerText:
            "Bei AKTE MORD seid ihr die Ermittler in einem spannenden Kriminalfall. Ihr erhaltet eine realistische Fallakte mit Beweisen, Vernehmungsprotokollen, Tatort-Fotos und vielen weiteren Hidden Clues. Ihr müsst auf eigene Faust die Dokumente nach Spuren durchsuchen und den Fall lösen.",
        },
        {
          questionName: "Mein Fall ist nicht komplett. Was kann ich tun?",
          acceptedAnswerText:
            "Unsere Fälle werden lokal vor Ort in Handarbeit zusammengetragen. Sollte versehentlich etwas fehlen, könnt ihr uns einfach unter mail@akte-mord.de kontaktieren und wir sorgen für Ersatz. Solltet ihr während eurer Ermittlungen feststellen, dass etwas fehlt: keine Panik! Wir haben für solche Fälle vorgesorgt. Jeder Akte liegt ein individueller Zugangscode bei. Damit könnt ihr bis zu fünf Materialien als PDF herunterladen, sodass ihr auf jeden Fall weiter ermitteln könnt.",
        },
        {
          questionName: "Was können wir tun, wenn wir bei einer Runde nicht weiterkommen?",
          acceptedAnswerText:
            "Für jede Runde könnt ihr bis zu drei Hinweise erhalten, solltet ihr einmal nicht weiterkommen. Jeder Hinweis bringt euch dabei näher an die Lösung. Nach den drei Hinweisen habt ihr die Möglichkeit, euch die Lösung der jeweiligen Runde anzeigen zu lassen.",
        },
        {
          questionName: "Für wie viele Personen sind eure Fälle geeignet?",
          acceptedAnswerText:
            "Unsere Fälle sind für 1 – 5 Personen entwickelt. Die Fälle können sowohl allein als auch in der Gruppe gelöst werden. Theoretisch lassen sich die Fälle auch mit mehr Personen lösen (es gibt hier keine harte Grenze), aber irgendwann werden die Diskussionen sicherlich unübersichtlich.",
        },
        {
          questionName: "Wie kommt es, dass eure Materialien so realistisch aussehen?",
          acceptedAnswerText:
            "Unser persönlicher Background liegt im Medien, Film- und Theaterbereich. Geschichten spannend und realistisch zu erzählen, haben wir als unser Handwerk gelernt. Bei der Entwicklung haben wir neben umfangreichen Recherchen mit Anwälten und Kriminalbeamten gesprochen, um einen möglichst realistischen Fall zu entwickeln. Um Verwechslungen auszuschließen, verwenden wir keine echten Wappen oder Siegel und haben auf allen Materialien vermerkt, dass es sich um fiktionales Spielmaterial handelt.",
        },
        {
          questionName: "Sind die Fälle mehrfach lösbar?",
          acceptedAnswerText:
            "Wie bei einem guten Krimi kennt ihr im Anschluss die Geschichte und wisst, wer der Täter bzw. die Täterin ist. Deshalb ist jeder Fall nur einmal lösbar. Da die Akte jedoch nicht beschädigt wird, könnt ihr den Fall an andere Ermittler weitergeben oder verschenken.",
        },
        {
          questionName: "Wie lange dauert es, bis ich meine Bestellung erhalte?",
          acceptedAnswerText:
            "In der Regel dauert es 3 – 5 Werktage, bis ihr eure Bestellung erhaltet. Genauere Informationen findet ihr hier. Solltet ihr eure Bestellung innerhalb der angegebenen Lieferzeit nicht erhalten haben, kontaktiert uns bitte.",
        },
        {
          questionName: "Wie lange dauern eure Fälle?",
          acceptedAnswerText:
            "Mit über 59 Materialien und zusätzlichen Audio-Dokumenten sind unsere Fälle sehr umfangreich. Die Lösung dauert in der Regel zwischen zweieinhalb und vier Stunden. Das hängt stark von der Gruppengröße und eurer Diskussionsfreude ab. Erfahrungsgemäß brauchen Gruppen länger als Einzelpersonen.",
        },
        {
          questionName: "Was wird für die Ermittlungen benötigt?",
          acceptedAnswerText:
            "Alles, was ihr braucht, ist ein Smartphone oder ein anderes internetfähiges Gerät (Computer, Tablet, etc.), um eure Lösung zu überprüfen. Ihr braucht also einen Internetzugang. Ansonsten sind keine Vorbereitungen für unsere Fälle notwendig und ihr müsst euch auch nicht bei irgendwelchen Plattformen / Anbietern anmelden.",
        },
        {
          questionName: "Für welches Alter sind die Fälle geeignet?",
          acceptedAnswerText: "Aufgrund des Inhalts empfehlen wir unsere Fälle erst ab 16 Jahren.",
        },
        {
          questionName: "Lässt sich auch über Skype oder Zoom ermitteln?",
          acceptedAnswerText:
            "Grundsätzlich könnt ihr von verschiedenen Orten aus über Skype oder Zoom gemeinsam ermitteln. Allerdings brauchen alle Teilnehmer eine eigene Akte, d. h. alle Ermittler müssen physischen Zugang zu den Beweisen und Materialien haben.",
        },
        {
          questionName: "Gibt es weitere Fälle?",
          acceptedAnswerText:
            "Weitere Fälle sind in Arbeit. Wir werden in 2024 weitere Fälle veröffentlichen. Folge uns auf Facebook oder Instagram, um von unseren Neuerscheinungen zu erfahren.",
        },
        {
          questionName: "Wer seid ihr?",
          acceptedAnswerText:
            "Wir sind ein kleiner, unabhängiger Verlag aus Berlin. Nachdem wir mit Culinario Mortale eine der erfolgreichsten Krimi-Dinner Reihen entwickelt haben, möchten wir mit der Tatort Krimi Reihe AKTE MORD nun andere Geschichten erzählen.",
        },
      ]}
    />
    <Container>
      <Typography variant="h1">Hilfe und Support zu unseren Fällen</Typography>
      <Typography variant="body1" paragraph>
        An dieser Stelle haben wir häufige Fragen zu unseren Cold Case Fällen zusammengestellt. Falls ihr
        eine Frage habt, die hier nicht aufgeführt ist, meldet euch bitte bei uns.
      </Typography>
      <Paper sx={{ p: { xs: 2, sm: 4 }, mx: { xs: -2, sm: 0 }, my: { xs: 2, md: 4 } }}>
        Sollte in eurer Akte etwas fehlen, sorgen wir so schnell wie möglich für Ersatz. Bitte schreibt
        uns unter <InlineLink href="mailto:mail@akte-mord.de">mail@akte-mord.de</InlineLink>. In
        Notfällen könnt ihr mit eurem Zugangscode{" "}
        <InternalInlineLink href="/hilfe/fehlende-materialien">fehlende Materialien</InternalInlineLink>{" "}
        als PDF-Datei herunterladen.
      </Paper>
      <FAQ
        question="Wie funktioniert Akte Mord?"
        answer={[
          "Bei AKTE MORD seid ihr die Ermittler in einem spannenden Kriminalfall. Ihr erhaltet eine realistische Fallakte mit Beweisen, Vernehmungsprotokollen, Tatort-Fotos und vielen weiteren Hidden Clues. Ihr müsst auf eigene Faust die Dokumente nach Spuren durchsuchen und den Fall lösen.",
        ]}
      />
      <FAQ
        question="Mein Fall ist nicht komplett. Was kann ich tun?"
        answer={[
          <React.Fragment key="">
            Unsere Fälle werden lokal vor Ort in Handarbeit zusammengetragen. Sollte versehentlich etwas
            fehlen, könnt ihr uns einfach unter{" "}
            <InlineLink href="mailto:mail@akte-mord.de">mail@akte-mord.de</InlineLink> kontaktieren und
            wir sorgen für Ersatz. Solltet ihr während eurer Ermittlungen feststellen, dass etwas fehlt:
            keine Panik! Wir haben für solche Fälle vorgesorgt. Jeder Akte liegt ein individueller
            Zugangscode bei. Damit könnt ihr bis zu fünf Materialien als PDF{" "}
            <InternalInlineLink href="/hilfe/fehlende-materialien">herunterladen</InternalInlineLink>,
            sodass ihr auf jeden Fall weiter ermitteln könnt.
          </React.Fragment>,
        ]}
      />
      <FAQ
        question="Was können wir tun, wenn wir bei einer Runde nicht weiterkommen?"
        answer={[
          "Für jede Runde könnt ihr bis zu drei Hinweise erhalten, solltet ihr einmal nicht weiterkommen. Jeder Hinweis bringt euch dabei näher an die Lösung. Nach den drei Hinweisen habt ihr die Möglichkeit, euch die Lösung der jeweiligen Runde anzeigen zu lassen.",
        ]}
      />
      <FAQ
        question="Für wie viele Personen sind eure Fälle geeignet?"
        answer={[
          "Unsere Fälle wurden für 1 – 5 Personen entwickelt. Die Fälle können sowohl allein als auch in der Gruppe gelöst werden. Theoretisch lassen sich die Fälle auch mit mehr Personen lösen (es gibt hier keine harte Grenze), aber irgendwann werden die Diskussionen sicherlich unübersichtlich.",
        ]}
      />
      <FAQ
        question="Wie kommt es, dass eure Materialien so realistisch aussehen?"
        answer={[
          "Unser persönlicher Background liegt im Medien, Film- und Theaterbereich. Geschichten spannend und realistisch zu erzählen, haben wir als unser Handwerk gelernt. Bei der Entwicklung haben wir neben umfangreichen Recherchen mit Anwälten und Kriminalbeamten gesprochen, um einen möglichst realistischen Fall zu entwickeln. Um Verwechslungen auszuschließen, verwenden wir keine echten Wappen oder Siegel und haben auf allen Materialien vermerkt, dass es sich um fiktionales Material handelt.",
        ]}
      />
      <FAQ
        question="Sind die Fälle mehrfach lösbar?"
        answer={[
          "Wie bei einem guten Krimi kennt ihr im Anschluss die Geschichte und wisst, wer der Täter bzw. die Täterin ist. Deshalb ist jeder Fall nur einmal lösbar. Da die Akte jedoch nicht beschädigt wird, könnt ihr den Fall an andere Ermittler weitergeben oder verschenken.",
        ]}
      />
      <FAQ
        question="Wie lange dauert es, bis ich meine Bestellung erhalte?"
        answer={[
          <React.Fragment key="">
            In der Regel dauert es 3 – 5 Werktage, bis ihr eure Bestellung erhaltet. Genauere
            Informationen findet ihr{" "}
            <InternalInlineLink href="/versandkosten-und-lieferzeiten">hier</InternalInlineLink>. Solltet
            ihr eure Bestellung innerhalb der angegebenen Lieferzeit nicht erhalten haben,{" "}
            <InlineLink href="mailto:mail@akte-mord.de">kontaktiert</InlineLink> uns bitte.
          </React.Fragment>,
        ]}
      />
      <FAQ
        question="Wie lange dauern eure Fälle?"
        answer={[
          "Mit über 59 Materialien und zusätzlichen Audio-Dokumenten sind unsere Fälle sehr umfangreich. Die Lösung dauert in der Regel zwischen zweieinhalb und vier Stunden. Das hängt stark von der Gruppengröße und eurer Diskussionsfreude ab. Erfahrungsgemäß brauchen Gruppen länger als Einzelpersonen.",
        ]}
      />
      <FAQ
        question="Was wird für die Ermittlungen benötigt?"
        answer={[
          "Alles, was ihr braucht, ist ein Smartphone oder ein anderes internetfähiges Gerät (Computer, Tablet, etc.), um eure Lösung zu überprüfen. Ihr braucht also einen Internetzugang. Ansonsten sind keine Vorbereitungen für unsere Fälle notwendig und ihr müsst euch auch nicht bei irgendwelchen Plattformen / Anbietern anmelden.",
        ]}
      />

      <FAQ
        question="Für welches Alter sind die Fälle geeignet?"
        answer={["Aufgrund des Inhalts empfehlen wir unsere Fälle erst ab 16 Jahren."]}
      />
      <FAQ
        question="Lässt sich auch über Skype oder Zoom ermitteln?"
        answer={[
          "Grundsätzlich könnt ihr von verschiedenen Orten aus über Skype oder Zoom gemeinsam ermitteln. Allerdings brauchen alle Teilnehmer eine eigene Akte, d. h. alle Ermittler müssen physischen Zugang zu den Beweisen und Materialien haben.",
        ]}
      />
      <FAQ
        question="Gibt es weitere Fälle?"
        answer={[
          "Weitere Fälle sind in Arbeit. Wir werden in 2024 weitere Fälle veröffentlichen. Folge uns auf Facebook oder Instagram, um von unseren Neuerscheinungen zu erfahren.",
        ]}
      />
      <FAQ
        question="Wer seid ihr?"
        answer={[
          <React.Fragment key="">
            Wir sind ein kleiner, unabhängiger Verlag aus Berlin. Nachdem wir mit{" "}
            <InlineLink
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.culinario-mortale.de"
            >
              Culinario Mortale
            </InlineLink>{" "}
            eine der erfolgreichsten Krimi-Dinner Reihen entwickelt haben, möchten wir mit der Tatort
            Krimi Reihe AKTE MORD nun andere Geschichten erzählen.
          </React.Fragment>,
        ]}
      />
    </Container>
  </React.Fragment>
);

Help.getLayout = (page) => (
  <BaseWithFollowUsLayout
    title="Hilfe zu unseren Cold Case Fällen | Akte Mord®"
    description="Häufige Fragen und Antworten zu unseren Cold Case Fällen."
  >
    {page}
  </BaseWithFollowUsLayout>
);

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: {
      ...(locale ? await serverSideTranslations(locale, ["c-common"], i18nConfig) : {}),
    },
  };
};

export default Help;
